import { useEffect, useState, type JSX } from 'react';
import { httpsUrl, isSingleEmoji } from '../../../core/channel';
import {
  ACCENT_COLOR_PATTERN,
  CLIP_SECONDS,
  CHANNEL_BIO_MAX,
  CHANNEL_DISPLAY_NAME_MAX,
  CHANNEL_LINK_TITLE_MAX,
  CHANNEL_LINKS_MAX,
  CLIP_EXCLUDED_CATEGORIES_MAX,
  CLIP_REACTION_EMOJIS_MAX,
  DEFAULT_ACCENT_COLOR,
  DEFAULT_CLIP_REACTION_EMOJIS,
  GOAL_TARGET_MAX,
  GOAL_TITLE_MAX,
  type ChannelLink,
  type ChannelProfile,
  type ClipSeconds,
  type ClipSettings,
  type RaidSettings,
  type StreamInfo,
} from '../../../core/model';
import { fetchChannel, fetchStreamInfo } from '../../../net/channel-api';
import { parseStreamKey } from '../../../net/swarm-publisher';
import { CategoryField } from '../../components/CategoryField';
import { categorySlugOf } from '../../components/categorySuggest';
import { Icon } from '../../components/Icon';
import { NumberField } from '../../components/NumberField';
import { useProject, useUiContext } from '../../context';
import { attempt, safely } from '../../safe';
import { CommitInput } from './CommitInput';
import { FeaturedSection, ScheduleSection } from './ScheduleSection';
import { ownTitle, useChannelTitle } from './channelTitle';
import { CountedText, StreamInfoForm } from './StreamInfoForm';

/** Why a link row cannot be saved, or null when it can. */
export function linkProblem(link: ChannelLink): string | null {
  if (!link.url.trim()) return 'Add an https:// address.';
  return httpsUrl(link.url) ? null : 'Links must be https:// addresses.';
}

/** Like Twitch: a display name may only change the capitalisation of the username. Null when it is fine. */
export function displayNameProblem(displayName: string, username: string | null): string | null {
  const name = displayName.trim();
  if (name === '' || username === null) return null;
  return name.toLowerCase() === username.toLowerCase() ? null : 'Display name can only change the capitalisation of your username';
}

/** The channel's registered name on the control plane, or null while unknown (no key, offline, unreachable). */
function useChannelUsername(apiUrl: string, streamKey: string): string | null {
  const [username, setUsername] = useState<string | null>(null);
  useEffect(() => {
    setUsername(null);
    if (!parseStreamKey(streamKey)) return;
    let cancelled = false;
    void (async () => {
      try {
        const stream = await fetchStreamInfo({ apiUrl, streamKey });
        if (!stream?.channelSlug || cancelled) return;
        const channel = await fetchChannel({ apiUrl, slug: stream.channelSlug });
        if (!cancelled) setUsername(channel?.name ?? null);
      } catch {
        // Unknown: the display name is not checked.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, streamKey]);
  return username;
}

const usableLinks = (rows: readonly ChannelLink[]): ChannelLink[] =>
  rows.filter((row) => linkProblem(row) === null).map((row) => ({ title: row.title.trim(), url: row.url.trim() }));

/**
 * Rows are edited locally and only complete https links are written to the project (the same
 * pattern as the ICE servers in the Stream tab).
 */
function SocialLinks({ links, onChange }: { links: ChannelLink[]; onChange(next: ChannelLink[]): void }): JSX.Element {
  const [rows, setRows] = useState<ChannelLink[]>(links);
  const storedKey = JSON.stringify(links);
  useEffect(() => {
    setRows((current) => (JSON.stringify(usableLinks(current)) === storedKey ? current : (JSON.parse(storedKey) as ChannelLink[])));
  }, [storedKey]);

  const update = (next: ChannelLink[]): void => {
    setRows(next);
    const valid = usableLinks(next);
    if (JSON.stringify(valid) !== storedKey) onChange(valid);
  };
  const replace = (index: number, patch: Partial<ChannelLink>): void => update(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <div className="ui-col" style={{ gap: 4 }}>
      {rows.length === 0 ? <p className="ui-dim">No social links yet.</p> : null}
      {rows.map((link, index) => {
        const problem = linkProblem(link);
        return (
          <div key={index}>
            <div className="channel-link-row">
              <CommitInput label={`Link ${index + 1} title`} placeholder="Title" value={link.title} onCommit={(title) => replace(index, { title: title.slice(0, CHANNEL_LINK_TITLE_MAX) })} />
              <CommitInput label={`Link ${index + 1} URL`} type="url" placeholder="https://" value={link.url} invalid={problem !== null && link.url !== ''} onCommit={(url) => replace(index, { url })} />
              <button type="button" className="ui-icon-btn" aria-label={`Remove link ${index + 1}`} title="Remove" onClick={() => update(rows.filter((_, i) => i !== index))}>
                <Icon name="trash" />
              </button>
            </div>
            {problem ? <div className="ui-warn-text">{problem} Not saved yet.</div> : null}
          </div>
        );
      })}
      <div>
        <button type="button" className="ui-btn" disabled={rows.length >= CHANNEL_LINKS_MAX} onClick={() => setRows([...rows, { title: '', url: '' }])}>
          <Icon name="plus" /> Add link
        </button>
      </div>
    </div>
  );
}

type ImageField = 'avatarUrl' | 'bannerUrl' | 'offlineImageUrl';

/** An https image URL: an invalid entry is flagged and kept out of the project. */
function ImageUrlField({ field, label, help, value, onCommit }: { field: ImageField; label: string; help?: string; value: string; onCommit(field: ImageField, url: string): void }): JSX.Element {
  const [problem, setProblem] = useState(false);
  const id = `channel-${field}`;
  return (
    <div className="ui-field">
      <label htmlFor={id}>{label}</label>
      <CommitInput
        id={id}
        label={label}
        type="url"
        placeholder="https://"
        value={value}
        invalid={problem}
        onCommit={(url) => {
          const bad = url.trim() !== '' && !httpsUrl(url);
          setProblem(bad);
          if (!bad) onCommit(field, url.trim());
        }}
      />
      {problem ? <span className="ui-field-help ui-warn-text">Use an https:// address. Not saved.</span> : null}
      {help ? <span className="ui-field-help">{help}</span> : null}
      {value && !problem ? <img className={`channel-preview ${field}`} src={value} alt={`${label} preview`} /> : null}
    </div>
  );
}

const RAID_ACCEPT: readonly [RaidSettings['accept'], string][] = [
  ['anyone', 'Accept raids from anyone'],
  ['following', 'Only from channels I follow'],
  ['nobody', 'Reject all raids'],
];

/** The follower goal shown on the channel page ("Manage goals"). */
function GoalsSection(): JSX.Element {
  const { studio } = useUiContext();
  const goal = useProject((p) => p.channel.goal);
  const setGoal = (next: typeof goal): void => void attempt(studio, () => studio.commands.setChannelGoal(next));
  return (
    <div className="ui-col" style={{ gap: 8 }}>
      <div className="ui-field">
        <span className="ui-field-label">Goal</span>
        <label className="ui-check">
          <input type="checkbox" checked={goal !== null} onChange={(e) => setGoal(e.target.checked ? { kind: 'followers', target: 100, title: '' } : null)} />
          Follower goal
        </label>
        <span className="ui-field-help">Shown on your channel page with your follower progress.</span>
      </div>
      {goal ? (
        <>
          <div className="ui-field">
            <label htmlFor="channel-goal-target">Target</label>
            <NumberField id="channel-goal-target" label="Goal target" value={goal.target} min={1} max={GOAL_TARGET_MAX} step={1} precision={0} onChange={(target) => setGoal({ ...goal, target })} />
          </div>
          <div className="ui-field">
            <label htmlFor="channel-goal-title">Title</label>
            <CountedText id="channel-goal-title" label="Goal title" value={goal.title} max={GOAL_TITLE_MAX} placeholder="Follower goal" onCommit={(title) => setGoal({ ...goal, title })} />
          </div>
        </>
      ) : null}
    </div>
  );
}

/** Which incoming raids the channel accepts ("Raid settings"). */
function RaidSection(): JSX.Element {
  const { studio } = useUiContext();
  const raids = useProject((p) => p.channel.raids);
  const set = (patch: Partial<RaidSettings>): void => void attempt(studio, () => studio.commands.setRaidSettings(patch));
  return (
    <div className="ui-col" style={{ gap: 8 }}>
      <div className="ui-field" style={{ alignItems: 'start' }}>
        <span className="ui-field-label">Incoming raids</span>
        <div role="radiogroup" aria-label="Incoming raids" className="ui-col" style={{ gap: 4 }}>
          {RAID_ACCEPT.map(([accept, label]) => (
            <label key={accept} className="ui-check">
              <input type="radio" name="channel-raids" checked={raids.accept === accept} onChange={() => set({ accept })} />
              {label}
            </label>
          ))}
        </div>
      </div>
      {raids.accept !== 'nobody' ? (
        <>
          <div className="ui-field">
            <label htmlFor="channel-raid-min">Minimum viewers</label>
            <NumberField id="channel-raid-min" label="Minimum viewers" value={raids.minViewers} min={0} max={100_000_000} step={1} precision={0} onChange={(minViewers) => set({ minViewers })} />
          </div>
          <div className="ui-field">
            <label htmlFor="channel-raid-max">Maximum viewers</label>
            <NumberField id="channel-raid-max" label="Maximum viewers" value={raids.maxViewers} min={0} max={100_000_000} step={1} precision={0} onChange={(maxViewers) => set({ maxViewers })} />
            <span className="ui-field-help">0 = no limit.</span>
          </div>
          <div className="ui-field">
            <label htmlFor="channel-raid-age">Minimum channel age</label>
            <NumberField id="channel-raid-age" label="Minimum channel age" value={raids.minChannelAgeDays} min={0} max={36_500} step={1} unit="days" precision={0} onChange={(minChannelAgeDays) => set({ minChannelAgeDays })} />
          </div>
        </>
      ) : null}
      <span className="ui-dim">Rules other channels must meet to send their viewers to you when they end a stream.</span>
    </div>
  );
}

const UNAVAILABLE: readonly string[] = [
  'Copyright audio warnings',
  'Server-side VoDs (recordings stay in this browser)',
  'Server transcoding (one rendition in v1)',
];

const RETENTION_DAYS: readonly [number, string][] = [
  [0, 'Forever'],
  [7, '7 days'],
  [14, '14 days'],
  [60, '60 days'],
  [365, '1 year'],
];

const WHO_CAN_CLIP: readonly [ClipSettings['whoCanClip'], string][] = [
  ['anyone', 'Anyone'],
  ['following', 'Followers only'],
];

/** Follow ages as Twitch lists them; the values are FOLLOW_AGE_MINUTES. */
const FOLLOW_AGES: readonly [number, string][] = [
  [0, 'Any follower'],
  [10, '10 minutes'],
  [30, '30 minutes'],
  [60, '1 hour'],
  [1440, '1 day'],
  [10080, '1 week'],
  [43200, '1 month'],
  [129600, '3 months'],
];

const CLIP_FLAGS: readonly ['captions' | 'reactions' | 'comments', string][] = [
  ['captions', 'Viewers can add captions'],
  ['reactions', 'Reactions on clips'],
  ['comments', 'Comments on clips'],
];

export { categorySlugOf };

/** Why the six reaction inputs cannot be saved, or null when they can. */
export function reactionsProblem(drafts: readonly string[]): string | null {
  const list = drafts.map((d) => d.trim()).filter((d) => d !== '');
  if (list.length === 0) return 'Keep at least one reaction.';
  if (list.some((d) => !isSingleEmoji(d))) return 'Each reaction must be a single emoji.';
  if (new Set(list).size !== list.length) return 'Each reaction must be different.';
  return null;
}

function padReactions(list: readonly string[]): string[] {
  return Array.from({ length: CLIP_REACTION_EMOJIS_MAX }, (_, i) => list[i] ?? '');
}

/** "Exclude categories": category slugs entered as tags, each with a remove button. */
function ExcludedCategories({ value, onChange }: { value: string[]; onChange(next: string[]): void }): JSX.Element {
  const apiUrl = useProject((p) => p.output.streaming.swarmApiUrl);
  const [draft, setDraft] = useState('');
  const full = value.length >= CLIP_EXCLUDED_CATEGORIES_MAX;
  const addSlug = (slug: string): void => {
    setDraft('');
    if (!slug || value.includes(slug) || full) return;
    onChange([...value, slug]);
  };
  const add = (): void => addSlug(categorySlugOf(draft));
  return (
    <div className="ui-col" style={{ gap: 4 }}>
      {value.length > 0 ? (
        <ul className="stream-tags" aria-label="Excluded categories">
          {value.map((slug) => (
            <li key={slug} className="stream-tag">
              <span>{slug}</span>
              <button type="button" className="stream-tag-remove" aria-label={`Remove ${slug}`} title="Remove" onClick={() => onChange(value.filter((s) => s !== slug))}>
                <Icon name="close" size={10} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="ui-row" style={{ alignItems: 'start' }}>
        <CategoryField
          className="ui-grow"
          label="Add excluded category"
          value={draft}
          apiUrl={apiUrl}
          live
          counter={false}
          placeholder={full ? `At most ${CLIP_EXCLUDED_CATEGORIES_MAX} categories` : 'Category, e.g. just-chatting'}
          disabled={full}
          onCommit={setDraft}
          onSelect={(choice) => addSlug(choice.slug)}
          onEnter={add}
        />
        <button type="button" className="ui-btn" disabled={full || categorySlugOf(draft) === ''} onClick={add}>
          <Icon name="plus" /> Add
        </button>
      </div>
    </div>
  );
}

/** "Clip reactions": six single-emoji inputs, saved together on blur or Enter when all are valid. */
function ReactionEmojis({ value, onChange }: { value: string[]; onChange(next: string[]): void }): JSX.Element {
  const [drafts, setDrafts] = useState(() => padReactions(value));
  const key = value.join(' ');
  useEffect(() => setDrafts(padReactions(key.split(' ').filter((e) => e !== ''))), [key]);
  const problem = reactionsProblem(drafts);
  const commit = (): void => {
    if (problem) return;
    const next = drafts.map((d) => d.trim()).filter((d) => d !== '');
    if (next.join(' ') !== key) onChange(next);
  };
  const isDefault = key === DEFAULT_CLIP_REACTION_EMOJIS.join(' ');
  return (
    <div className="ui-col" style={{ gap: 4 }}>
      <div className="ui-row" style={{ flexWrap: 'wrap', gap: 4 }}>
        {drafts.map((draft, i) => {
          const bad = draft.trim() !== '' && !isSingleEmoji(draft.trim());
          return (
            <input
              key={i}
              className={bad ? 'ui-input invalid' : 'ui-input'}
              style={{ width: 48, textAlign: 'center' }}
              aria-label={`Reaction ${i + 1}`}
              aria-invalid={bad || undefined}
              autoComplete="off"
              spellCheck={false}
              value={draft}
              onChange={(e) => {
                const text = e.target.value;
                setDrafts((d) => d.map((x, j) => (j === i ? text : x)));
              }}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commit();
              }}
            />
          );
        })}
        <button type="button" className="ui-btn" disabled={isDefault} onClick={() => onChange([...DEFAULT_CLIP_REACTION_EMOJIS])}>
          Reset to default
        </button>
      </div>
      {problem ? (
        <span className="ui-warn-text" role="alert">
          {problem}
        </span>
      ) : (
        <span className="ui-field-help">Viewers react to clips with these emoji. Leave a box empty to offer fewer.</span>
      )}
    </div>
  );
}

/** Replay-buffer and viewer clips ("Clip settings"). */
function ClipsSection(): JSX.Element {
  const { studio } = useUiContext();
  const clips = useProject((p) => p.channel.clips);
  const set = (patch: Partial<ClipSettings>): void => void attempt(studio, () => studio.commands.setClipSettings(patch));
  const followersOnly = clips.whoCanClip === 'following' ? String(clips.minFollowMinutes) : 'no';
  return (
    <>
      <div className="ui-field">
        <span className="ui-field-label">Clips</span>
        <label className="ui-check">
          <input
            type="checkbox"
            checked={clips.enabled}
            onChange={(e) => {
              const enabled = e.target.checked;
              attempt(studio, () => studio.commands.setClipSettings({ enabled }));
            }}
          />
          Enable clips
        </label>
        <span className="ui-field-help">Keeps a replay buffer of the program while you stream or record; Make a clip saves the last moments as a recording.</span>
      </div>
      <div className="ui-field">
        <label htmlFor="channel-clip-seconds">Clip length</label>
        <select
          id="channel-clip-seconds"
          className="ui-select"
          aria-label="Clip length"
          value={clips.seconds}
          onChange={(e) => {
            const seconds = Number(e.target.value) as ClipSeconds;
            attempt(studio, () => studio.commands.setClipSettings({ seconds }));
          }}
        >
          {CLIP_SECONDS.map((seconds) => (
            <option key={seconds} value={seconds}>
              {seconds} s
            </option>
          ))}
        </select>
        <span className="ui-field-help">
          A clip covers at least this much and up to twice it. Viewers clip from the segments their player already holds; the clip
          is uploaded to the control plane and shown on your channel page.
        </span>
      </div>
      <div className="ui-field" style={{ alignItems: 'start' }}>
        <span className="ui-field-label">Viewer clips</span>
        <div className="ui-col" style={{ gap: 4 }}>
          {CLIP_FLAGS.map(([flag, label]) => (
            <label key={flag} className="ui-check">
              <input type="checkbox" checked={clips[flag]} onChange={(e) => set({ [flag]: e.target.checked })} />
              {label}
            </label>
          ))}
        </div>
      </div>
      <div className="ui-field">
        <label htmlFor="channel-clip-followers-only">Followers only</label>
        <select
          id="channel-clip-followers-only"
          className="ui-select"
          aria-label="Followers only"
          value={followersOnly}
          onChange={(e) => {
            const v = e.target.value;
            set(v === 'no' ? { whoCanClip: 'anyone' } : { whoCanClip: 'following', minFollowMinutes: Number(v) });
          }}
        >
          <option value="no">No</option>
          {FOLLOW_AGES.map(([minutes, label]) => (
            <option key={minutes} value={String(minutes)}>
              {label}
            </option>
          ))}
        </select>
        <span className="ui-field-help">Only followers can clip, optionally only after following for this long.</span>
      </div>
      <div className="ui-field" style={{ alignItems: 'start' }}>
        <span className="ui-field-label">Exclude categories</span>
        <ExcludedCategories value={clips.excludedCategories} onChange={(excludedCategories) => set({ excludedCategories })} />
        <span className="ui-field-help">No clips while you stream in these categories</span>
      </div>
      <div className="ui-field" style={{ alignItems: 'start' }}>
        <span className="ui-field-label">Comment access</span>
        <div className="ui-col" style={{ gap: 4 }}>
          <div role="radiogroup" aria-label="Comment access" className="ui-row" style={{ gap: 12 }}>
            {WHO_CAN_CLIP.map(([who, label]) => (
              <label key={who} className="ui-check">
                <input type="radio" name="channel-comments-who" checked={clips.commentsWho === who} onChange={() => set({ commentsWho: who })} />
                {label}
              </label>
            ))}
          </div>
          {clips.commentsWho === 'following' ? (
            <select
              className="ui-select"
              aria-label="Comment follow age"
              value={clips.commentsMinFollowMinutes}
              onChange={(e) => set({ commentsMinFollowMinutes: Number(e.target.value) })}
            >
              {FOLLOW_AGES.map(([minutes, label]) => (
                <option key={minutes} value={minutes}>
                  {label}
                </option>
              ))}
            </select>
          ) : null}
        </div>
        <span className="ui-field-help">Who can comment on your clips.</span>
      </div>
      <div className="ui-field" style={{ alignItems: 'start' }}>
        <span className="ui-field-label">Clip reactions</span>
        <ReactionEmojis value={clips.reactionEmojis} onChange={(reactionEmojis) => set({ reactionEmojis })} />
      </div>
    </>
  );
}

export function ChannelTab(): JSX.Element {
  const { studio, ui } = useUiContext();
  const channel = useProject((p) => p.channel);
  const streaming = useProject((p) => p.output.streaming);
  const video = useProject((p) => p.video);
  const [showKey, setShowKey] = useState(false);
  const { profile } = channel;
  const username = useChannelUsername(streaming.swarmApiUrl, streaming.swarmStreamKey.trim());
  const [nameProblem, setNameProblem] = useState<string | null>(null);
  const portal = channel.portalUrl.replace(/\/+$/, '');

  // The title box starts from the channel's title; only one typed over it becomes the studio's.
  const channelTitle = useChannelTitle();
  const setInfo = (next: StreamInfo): void => {
    attempt(studio, () => studio.commands.setStreamInfo({ ...next, title: channel.info.title === '' ? ownTitle(next.title, channelTitle) : next.title }));
    void safely(studio, () => studio.updateStreamInfo(), 'Could not update the stream info');
  };
  const setProfile = (patch: Partial<ChannelProfile>): void => void attempt(studio, () => studio.commands.setChannelProfile(patch));
  const hasKey = streaming.swarmStreamKey.trim() !== '';
  const [hex, setHex] = useState(profile.accentColor);
  useEffect(() => setHex(profile.accentColor), [profile.accentColor]);
  const hexProblem = !ACCENT_COLOR_PATTERN.test(hex.trim());

  const publish = (): void =>
    void safely(
      studio,
      () => studio.publishChannelProfile().then(() => studio.notify('info', 'Channel profile published to ompeer.com.')),
      'Could not publish the channel profile',
    );
  const copyKey = (): void =>
    void safely(studio, async () => {
      await navigator.clipboard.writeText(streaming.swarmStreamKey);
      studio.notify('info', 'Stream key copied.');
    }, 'Could not copy');

  return (
    <div>
      <div className="ui-section-title">Stream info</div>
      <StreamInfoForm value={{ ...channel.info, title: channel.info.title || channelTitle }} onChange={setInfo} />

      <div className="ui-section-title">About</div>
      <div className="ui-col" style={{ gap: 8 }}>
        <div className="ui-field">
          <span className="ui-field-label">Username</span>
          <span className="ui-mono" aria-label="Username">
            {username ?? '—'}
          </span>
          <span className="ui-field-help">
            Your channel's name and address.{' '}
            <a href={`${portal}/settings/profile`} target="_blank" rel="noopener noreferrer">
              Manage on the portal
            </a>
          </span>
        </div>
        <div className="ui-field">
          <label htmlFor="channel-display-name">Display name</label>
          <CountedText
            id="channel-display-name"
            label="Display name"
            value={profile.displayName}
            max={CHANNEL_DISPLAY_NAME_MAX}
            onCommit={(displayName) => {
              const problem = displayNameProblem(displayName, username);
              setNameProblem(problem);
              if (!problem) setProfile({ displayName });
            }}
          />
          {nameProblem ? (
            <span className="ui-field-help ui-warn-text" role="alert">
              {nameProblem}
            </span>
          ) : null}
          <span className="ui-field-help">Change the capitalisation of your username; leave empty to show the username.</span>
        </div>
        <div className="ui-field" style={{ alignItems: 'start' }}>
          <label htmlFor="channel-bio">Bio</label>
          <CountedText id="channel-bio" label="Bio" multiline value={profile.bio} max={CHANNEL_BIO_MAX} onCommit={(bio) => setProfile({ bio })} />
        </div>
        <div className="ui-field" style={{ alignItems: 'start' }}>
          <span className="ui-field-label">Social links</span>
          <SocialLinks links={profile.links} onChange={(links) => setProfile({ links })} />
        </div>
      </div>

      <div className="ui-section-title">Brand</div>
      <div className="ui-col" style={{ gap: 8 }}>
        <div className="ui-field">
          <label htmlFor="channel-accent">Accent color</label>
          <div className="ui-row">
            <input
              id="channel-accent"
              type="color"
              className="channel-color"
              aria-label="Accent color picker"
              value={ACCENT_COLOR_PATTERN.test(profile.accentColor) ? profile.accentColor : DEFAULT_ACCENT_COLOR}
              onChange={(e) => setProfile({ accentColor: e.target.value })}
            />
            <div className="ui-grow">
              <CommitInput
                label="Accent color"
                placeholder={DEFAULT_ACCENT_COLOR}
                value={profile.accentColor}
                invalid={hexProblem}
                onCommit={(value) => {
                  setHex(value);
                  if (ACCENT_COLOR_PATTERN.test(value.trim())) setProfile({ accentColor: value.trim() });
                }}
              />
            </div>
          </div>
          {hexProblem ? <span className="ui-field-help ui-warn-text">Use a #RRGGBB colour. Not saved.</span> : null}
        </div>
        <ImageUrlField field="avatarUrl" label="Avatar URL" value={profile.avatarUrl} onCommit={(field, url) => setProfile({ [field]: url })} />
        <ImageUrlField
          field="bannerUrl"
          label="Banner URL"
          help="1200×480 recommended; when empty the portal generates a banner from the accent color."
          value={profile.bannerUrl}
          onCommit={(field, url) => setProfile({ [field]: url })}
        />
        <ImageUrlField
          field="offlineImageUrl"
          label="Offline image URL"
          help="Shown in the player while the channel is offline."
          value={profile.offlineImageUrl}
          onCommit={(field, url) => setProfile({ [field]: url })}
        />
      </div>

      <div className="ui-section-title">Schedule</div>
      <ScheduleSection />

      <div className="ui-section-title">Featured content</div>
      <FeaturedSection />

      <div className="ui-section-title">Goals</div>
      <GoalsSection />

      <div className="ui-section-title">Publish</div>
      <div className="ui-col" style={{ gap: 4 }}>
        <div>
          <button type="button" className="ui-btn primary" disabled={!hasKey} onClick={publish}>
            <Icon name="globe" /> Publish channel profile
          </button>
        </div>
        {hasKey ? null : <span className="ui-warn-text">Paste your ompeer.com stream key in the Stream tab first.</span>}
        <span className="ui-dim">The profile is stored in this collection; publishing sends it to your channel page.</span>
      </div>

      <div className="ui-section-title">Stream settings</div>
      <div className="ui-col" style={{ gap: 8 }}>
        <div className="ui-field">
          <label htmlFor="channel-stream-key">Stream key</label>
          <div className="ui-row">
            <input
              id="channel-stream-key"
              className="ui-input ui-mono ui-grow"
              readOnly
              aria-label="Stream key"
              type={showKey ? 'text' : 'password'}
              placeholder="Not set"
              value={streaming.swarmStreamKey}
            />
            <button type="button" className="ui-btn" aria-pressed={showKey} onClick={() => setShowKey(!showKey)}>
              <Icon name={showKey ? 'eyeOff' : 'eye'} /> {showKey ? 'Hide' : 'Show'}
            </button>
            <button type="button" className="ui-btn" disabled={!hasKey} onClick={copyKey}>
              <Icon name="copy" /> Copy
            </button>
          </div>
          <span className="ui-field-help">
            Keep it private. To reset it, go to{' '}
            <a href={`${portal}/settings/stream`} target="_blank" rel="noopener noreferrer">
              Settings → Stream on the portal
            </a>
            .
          </span>
        </div>
        <div className="ui-field" style={{ alignItems: 'start' }}>
          <span className="ui-field-label">Latency mode</span>
          <div role="radiogroup" aria-label="Latency mode" className="ui-col" style={{ gap: 4 }}>
            <label className="ui-check">
              <input
                type="radio"
                name="channel-latency"
                checked={streaming.swarmSegmentMs === 200}
                onChange={() => void attempt(studio, () => studio.commands.setStreamingSettings({ swarmSegmentMs: 200 }))}
              />
              Lowest latency (200 ms parts, AV1 when available): the default
            </label>
            <label className="ui-check">
              <input
                type="radio"
                name="channel-latency"
                checked={streaming.swarmSegmentMs === 1000}
                onChange={() => void attempt(studio, () => studio.commands.setStreamingSettings({ swarmSegmentMs: 1000 }))}
              />
              Low latency (1 s segments): best for interacting with viewers
            </label>
            <label className="ui-check">
              <input
                type="radio"
                name="channel-latency"
                checked={streaming.swarmSegmentMs === 2000}
                onChange={() => void attempt(studio, () => studio.commands.setStreamingSettings({ swarmSegmentMs: 2000 }))}
              />
              Normal latency (2 s segments): smoother on weak networks
            </label>
          </div>
        </div>
        <div className="ui-field">
          <span className="ui-field-label">Past broadcasts</span>
          <label className="ui-check">
            <input
              type="checkbox"
              checked={channel.saveBroadcasts}
              onChange={(e) => {
                const saveBroadcasts = e.target.checked;
                attempt(studio, () => studio.commands.setChannelSettings({ saveBroadcasts }));
              }}
            />
            Save past broadcasts
          </label>
          <span className="ui-field-help">Records the program locally for as long as the stream runs. Find the files under File → Show recordings.</span>
        </div>
        <div className="ui-field">
          <label htmlFor="channel-vod-retention">Keep saved broadcasts for</label>
          <select
            id="channel-vod-retention"
            className="ui-select"
            aria-label="Keep saved broadcasts for"
            value={RETENTION_DAYS.some(([days]) => days === channel.vodRetentionDays) ? channel.vodRetentionDays : ''}
            onChange={(e) => {
              const vodRetentionDays = Number(e.target.value);
              attempt(studio, () => studio.commands.setChannelSettings({ vodRetentionDays }));
            }}
          >
            {RETENTION_DAYS.some(([days]) => days === channel.vodRetentionDays) ? null : (
              <option value="" disabled>
                {channel.vodRetentionDays} days
              </option>
            )}
            {RETENTION_DAYS.map(([days, label]) => (
              <option key={days} value={days}>
                {label}
              </option>
            ))}
          </select>
          <span className="ui-field-help">Older recordings and clips are deleted when the studio starts.</span>
        </div>
        <ClipsSection />
        <div className="ui-field">
          <span className="ui-field-label">2K</span>
          <div className="ui-col" style={{ gap: 4 }}>
            <span>2K (1440p) streaming: set the canvas to 2560×1440 in Settings → Video</span>
            <span className="ui-dim" aria-label="Current canvas">
              Current canvas: {video.width}×{video.height}
            </span>
            <div>
              <button type="button" className="ui-btn" onClick={() => ui.getState().openDialog({ type: 'settings', tab: 'video' })}>
                Open video settings
              </button>
            </div>
          </div>
        </div>
        <div className="ui-field">
          <span className="ui-field-label">Disconnect protection</span>
          <label className="ui-check">
            <input
              type="checkbox"
              checked={channel.disconnectProtection}
              onChange={(e) => {
                const disconnectProtection = e.target.checked;
                attempt(studio, () => studio.commands.setChannelSettings({ disconnectProtection }));
              }}
            />
            Disconnect protection
          </label>
          <span className="ui-field-help">Keeps the stream on air for 90 seconds if this studio drops, so viewers wait instead of seeing it end.</span>
        </div>
        <div className="ui-field">
          <label htmlFor="channel-portal">Portal URL</label>
          <CommitInput
            id="channel-portal"
            label="Portal URL"
            type="url"
            placeholder="https://ompeer.com"
            value={channel.portalUrl}
            onCommit={(portalUrl) => void attempt(studio, () => studio.commands.setChannelSettings({ portalUrl }))}
          />
          <span className="ui-field-help">Where your channel page and chat live.</span>
        </div>
      </div>

      <div className="ui-section-title">Raid settings</div>
      <RaidSection />
      <div className="channel-unavailable" aria-label="Not available in the browser studio">
        <div className="ui-dim">Not available in the browser studio</div>
        <ul className="ui-dim">
          {UNAVAILABLE.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
